/**
 * <banner-hero> — full-width hero (single static media; no slideshow).
 * Hydrates the high-quality image variant once the hero enters the viewport.
 *
 * Loaded ONLY from `sections/section-full-width-media.liquid` via
 * `type="module" fetchpriority="low"`.
 *
 * Dependencies:
 *   - @theme/responsive
 */

import { responsive } from '@theme/responsive';

class Banner extends HTMLElement {
  #observer = null;
  #initialized = false;

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.5 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#initialized = false;
  }

  #onIntersect = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      this.#upgradeImage();
      this.#observer.unobserve(this);
    });
  };

  #upgradeImage() {
    const breakpoint = responsive.getCurrentBreakpoint();
    const isMobile = breakpoint === 'pocket' || breakpoint === 'phone' || breakpoint === 'tablet';

    const imageContainers = Array.from(this.querySelectorAll('.image__element')).slice(0, 2);
    if (imageContainers.length === 0) return;

    const target = isMobile
      ? imageContainers[0]
      : (imageContainers.length === 2 ? imageContainers[1] : imageContainers[0]);

    if (!target || target.dataset.loadedImage === 'true') return;
    if (!target.dataset.src) return;

    target.src = target.dataset.src.replace('{width}x', 'width=2600');
    target.dataset.loadedImage = 'true';
  }
}

if (!customElements.get('banner-hero')) {
  customElements.define('banner-hero', Banner);
}
